import React, { useEffect, useState } from 'react';
import { Alert, Box, Snackbar, Typography } from '@mui/material';
import { Field, FormikProvider, useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import * as Yup from 'yup';
import { AppDispatch, RootState } from '../../../redux/store';
import api from '../../../redux/features/auth/axiosAuthHelper';
import { fetchUserProfile } from '../../../redux/features/auth/authSlice';
import { TextEditField } from '../fields/fields';
import RareButton from '../buttons/RareButton';
import StatusChip from '../StatusChip';
import SignInDialog from '../SignInDialog';
import colorConfigs from '../../../configs/colorConfigs';
import { userTypeLabel } from '../../../services/userRoles';

interface EditProfileCardProps {
  setPopup: (detail: string) => void;
  handleBackButton: () => void;
}

const EditProfileCard: React.FC<EditProfileCardProps> = ({ setPopup, handleBackButton }) => {
  const authData = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch<AppDispatch>();
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const profile = authData.user;

  useEffect(() => {
    if (!saved) return;
    const timeout = window.setTimeout(() => setPopup(''), 1500);
    return () => window.clearTimeout(timeout);
  }, [saved, setPopup]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      first_name: profile?.first_name ?? '',
      last_name: profile?.last_name ?? '',
      email: profile?.email ?? '',
    },
    validationSchema: Yup.object({
      first_name: Yup.string().trim().required('First name is required'),
      last_name: Yup.string().trim().required('Last name is required'),
      email: Yup.string().trim().email('Enter a valid email address').required('Email is required'),
    }),
    onSubmit: async (values) => {
      setError(null);
      try {
        await api.patch('profile/', {
          first_name: values.first_name.trim(),
          last_name: values.last_name.trim(),
          email: values.email.trim(),
        }, { headers: { Authorization: `Bearer ${authData.access}` } });
        void dispatch(fetchUserProfile());
        setSaved(true);
      } catch (failure: any) {
        const data = failure.response?.data;
        if (failure.response?.status === 400 && data && typeof data === 'object') {
          const field = Object.keys(data)[0];
          const detail = data[field];
          setError(`${field}: ${Array.isArray(detail) ? detail.join(', ') : detail}`);
        } else {
          setError('The profile could not be updated. Please try again.');
        }
      }
    },
  });

  if (!authData.isAuthenticated) return <SignInDialog isAuthenticated={authData.isAuthenticated} />;

  const stats = [
    ['User type', userTypeLabel(profile?.user_type ?? '')],
    ['Date created', profile?.date_joined ? dayjs(profile.date_joined).format('YYYY-MM-DD HH:mm:ss') : '—'],
    ['Last login', profile?.last_login ? dayjs(profile.last_login).format('YYYY-MM-DD HH:mm:ss') : 'Not yet logged in'],
  ];

  return (
    <FormikProvider value={formik}>
      <Box component="form" onSubmit={formik.handleSubmit} sx={{ px: { xs: 1, sm: 2 }, pt: 1, pb: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <Field name="first_name" type="text" label="First Name" autoComplete="given-name" component={TextEditField} />
            <Field name="last_name" type="text" label="Last Name" autoComplete="family-name" component={TextEditField} />
          </Box>
          <Field name="email" type="email" label="Email" autoComplete="email" component={TextEditField} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) minmax(0, 1.3fr) minmax(0, 1.3fr) auto' }, gap: 2, color: '#58646a', borderTop: '1px solid #e1e8e6', pt: 2 }}>
          {stats.map(([label, value]) => (
            <Box key={label} sx={{ minWidth: 0 }}>
              <Typography variant="caption" display="block">{label}</Typography>
              <Typography variant="body2" sx={{ color: colorConfigs.brand.logoText, overflowWrap: 'anywhere' }}>{value}</Typography>
            </Box>
          ))}
          <Box sx={{ justifySelf: 'end', alignSelf: 'end' }}>
            <StatusChip enabled={Boolean(profile?.is_active)} trueLabel="Active" falseLabel="Inactive" />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, borderTop: '1px solid #e1e8e6', pt: 1.5 }}>
          <RareButton variant="outlined" onClick={handleBackButton}>Back</RareButton>
          <RareButton variant="success" type="submit" disabled={!formik.dirty || formik.isSubmitting || saved}>Save Changes</RareButton>
        </Box>
      </Box>
      <Snackbar open={saved} autoHideDuration={1500} message="Profile successfully updated" />
    </FormikProvider>
  );
};

export default EditProfileCard;
